import React, { useEffect, useRef, useState } from 'react';
import ArrowRightIcon from '../ArrowRightIcon';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getUpcomingRenewals } from '../../../services/subscriptionService';
// Loader removed from this widget per request

const UpcomingRenewals = ({ selectedFrequency }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state?.authReducer?.tokenData?.token);
  const [renewals, setRenewals] = useState([]);
  const inFlightRef = useRef(false);

  useEffect(() => {
    const fetchRenewals = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        // Respect the dashboard frequency selection if available
        let timeframe = 'month';
        const tfInput = (selectedFrequency || (typeof window !== 'undefined' && window.__ADMIN_SELECTED_FREQUENCY__)) || 'Monthly';
        const tf = String(tfInput || '').toLowerCase();
        const map = { today: 'today', weekly: 'week', monthly: 'month', yearly: 'year' };
        timeframe = map[tf] || tf || 'month';
        const res = await dispatch(getUpcomingRenewals({ timeframe }))?.unwrap();
        if (!res?.success) {
          setRenewals([]);
          return;
        }

        // Backend may return grouped arrays keyed by timeframe
        const root = res?.data || {};
        const data = root?.data && typeof root?.data === 'object' ? root.data : root;

        // Support both "month/monthly", "week/weekly", etc.
        const tfKeyMap = { today: 'today', week: 'weekly', month: 'monthly', year: 'yearly' };
        const tfAltKeyMap = { today: 'today', week: 'week', month: 'month', year: 'year' };
        const tfKey = tfKeyMap[timeframe] || 'monthly';
        const tfAltKey = tfAltKeyMap[timeframe] || 'month';

        const candidates = [
          tfKey,
          tfAltKey,
          timeframe, // e.g. "month"
          // Common fallbacks
          'upcomingRenewals',
          'renewals',
          'items',
        ];

        let grouped = undefined;
        for (const k of candidates) {
          if (Array.isArray(data?.[k])) {
            grouped = data[k];
            break;
          }
        }

        // Fallback to flat structures
        let list = [];
        if (Array.isArray(grouped)) {
          list = grouped;
        } else if (Array.isArray(data)) {
          list = data;
        } else if (Array.isArray(data?.items)) {
          list = data.items;
        }

        // Important: backend may group by subscription interval (e.g. interval="year")
        // while still being requested with timeframe="month".
        // If the timeframe bucket is empty but other buckets have data, merge them.
        if (!list.length && data && typeof data === "object") {
          const mergedBuckets = Object.values(data).filter(Array.isArray).flat();
          if (mergedBuckets.length) {
            list = mergedBuckets;
          }
        }

        setRenewals(
          (Array.isArray(list) ? list : []).map((item) => ({
            username: item?.user?.name || item?.username || item?.name || '',
            email: item?.user?.email || item?.email || '',
            fundraiser:
              item?.fundraiser?.organizationName ||
              item?.fundraiser?.businessName ||
              item?.fundraiser?.user?.name ||
              item?.fundraiser?.name ||
              item?.fundraiser ||
              '',
            subscriptionId: item?.subscription || item?.subscriptionId || item?.subscription?.id || item?.stripeSubscriptionId || '',
          }))
        );
      } catch (_) {}
      inFlightRef.current = false;
    };
    if (!token) return;
    fetchRenewals();
  }, [dispatch, selectedFrequency, token]);

  return (
    <div className="summary w-full p-4 h-[470px] flex flex-col bg-white rounded-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-[20px] font-poppins font-[600] text-[#303030] leading-none">Upcoming Renewals (30 Days)</h2>
        <button onClick={() => navigate('/admin/user-list?upcomingRenewals=30d')} className="text-[#00ADEF] text-[16px] font-poppins font-[500] border border-[#00ADEF] rounded-[6px] px-4 py-2 transition hover:bg-[#E6F7FB] flex items-center">
          View All
          <ArrowRightIcon className="ml-2" />
        </button>
      </div>
      <>
          <table className="w-full table-fixed">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="w-[20%] text-left py-3 px-4 text-[14px] font-poppins font-[500] text-[#303030] whitespace-nowrap">Username</th>
                <th className="w-[30%] text-left py-3 px-4 text-[14px] font-poppins font-[500] text-[#303030] whitespace-nowrap">Email</th>
                <th className="w-[30%] text-left py-3 px-4 text-[14px] font-poppins font-[500] text-[#303030] whitespace-nowrap">Fundraiser</th>
                <th className="w-[20%] text-left py-3 px-4 text-[14px] font-poppins font-[500] text-[#303030] whitespace-nowrap">Subscription</th>
              </tr>
            </thead>
          </table>
          <div className="overflow-y-auto overflow-x-hidden h-[280px]">
            <table className="w-full table-fixed">
              <tbody>
                {(Array.isArray(renewals) ? renewals : []).map((renewal, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="w-[20%] py-4 px-4 text-[14px] font-poppins text-[#767676] whitespace-nowrap align-middle">
                      {(renewal?.username || '').length > 11 ? `${(renewal?.username || '').substring(0, 11)}...` : (renewal?.username || '')}
                    </td>
                    <td className="w-[30%] py-4 px-4 text-[14px] font-poppins text-[#767676] whitespace-nowrap align-middle">{renewal?.email || ''}</td>
                    <td className="w-[30%] py-4 px-4 text-[14px] font-poppins text-[#767676] whitespace-nowrap align-middle">{renewal?.fundraiser || ''}</td>
                    <td className="w-[20%] py-4 px-4 text-[14px] font-poppins text-[#767676] whitespace-nowrap align-middle">{renewal?.subscriptionId || renewal?.subscription || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!renewals?.length && (
            <div className="w-full h-full flex items-center justify-center text-[#767676] text-[14px] py-6">No data</div>
          )}
      </>
    </div>
  );
};

export default UpcomingRenewals; 
